import { useState } from 'react';
import { useAuth } from '../../app/auth/AuthProvider';
import { LiftXLogo } from '../../ui/components/LiftXLogo';
import { CompanyMark } from '../../ui/components/CompanyMark';
import { COMPANY_NAME } from '../../app/appInfo';
import styles from './LoginScreen.module.css';

/**
 * Shown when the server confirmed the sign-in, but this device could not be
 * safely switched to it — Phase G fix design ("cross-account local-storage
 * race"). `resetLocalDatabase()` (and its one automatic retry) failed while
 * clearing a previous account's local data, so this device's tables are in
 * an unverified state. Deliberately NOT the login screen (the credentials
 * already worked) and NOT `OfflineUnverifiableScreen` (this has nothing to
 * do with connectivity) — granting access anyway would let the sync
 * scheduler start against data nobody has confirmed is safe.
 *
 * "Try again" re-runs `revalidate()`, which re-attempts the same reset.
 */
export function ResetFailedScreen(): React.JSX.Element {
  const { revalidate } = useAuth();
  const [retrying, setRetrying] = useState(false);

  async function handleRetry(): Promise<void> {
    setRetrying(true);
    try {
      await revalidate();
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card} role="alert">
        <div className={styles.logoWrap}>
          <LiftXLogo height={44} />
        </div>

        <h1 className={styles.title}>Couldn&rsquo;t prepare this device</h1>
        <p className={styles.subtitle}>
          <CompanyMark size={14} className={styles.subtitleMark} />
          Personal access · {COMPANY_NAME}
        </p>

        <p className={styles.note}>
          LiftX couldn&rsquo;t safely switch this device over to your account.
          Nothing was synced. Try again — this usually resolves on retry.
        </p>

        <button
          type="button"
          className={styles.submit}
          onClick={() => void handleRetry()}
          disabled={retrying}
        >
          {retrying ? 'Retrying…' : 'Try again'}
        </button>
      </div>
    </div>
  );
}
